/* ═══════════════════════════════════════════════════════════════════════
   Formatting Artifacts — the pre-format review reader.

   Rebuilt 2026-08-22 on `docx-preview` (Apache-2.0); reworked again for
   formatting-artifacts R1 (2026-09-03) to run BEFORE formatting rather than
   after. This renders the REAL document — real fonts, real indents, real
   numbering, real tables — and merely DECORATES the flagged paragraphs on
   top of it.

   Flow: the pre-format scan flags artifacts (unheadered paragraphs, stray
   blank paragraphs, manual page breaks, nonstandard section ends, leftover
   pre-body lines). Live review renders the ORIGINAL input file (with the
   tracked-changes gate's revisionAction already applied in memory, so the
   scan's item ids still line up) and walks the user through each one —
   scroll to it, decide it, next — auto-advancing file to file and handing
   the decision map back before a single byte is formatted. The same
   component also serves a READ-ONLY reopen of an already-formatted file's
   residual "Artifacts" badge, which renders the OUTPUT instead.

   Fallbacks, in order: the document renders → guided reader. The document
   can't be read (web build's DesktopOnly, missing file, corrupt package) →
   the flat flagged-item list, where every decision is still reachable.
   ══════════════════════════════════════════════════════════════════════ */

import React from 'react';
import { renderAsync } from 'docx-preview';
import { ChevronDown, ChevronUp, FileText, Flag, Loader2 } from 'lucide-react';
import { Icon } from './ui/Icon';
import { rem } from '../lib/rem';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { StatusBadge } from './StatusBadge';
import { ZoomControl } from './ui/ZoomControl';
import { getUiScale, subscribeUiScale } from '../lib/uiScale';
import { usePref } from '../lib/usePref';
import type { DirtyItem, DirtyItemKind, LevelPreview } from '../../shared/forma';
import type { SpecFormaApi } from '../../shared/ipc-api';
import {
  READER_RENDER_OPTIONS,
  applyPostFixPreview,
  applyRemovedPreview,
  base64ToArrayBuffer,
  clearPostFixPreview,
  collectRenderedParagraphs,
  encloseHangingIndent,
  headingLevelNumber,
  itemRenderedIndex,
  numberFromSample,
  outlineCounters,
  postFixPreview,
  renderedParagraphLevel,
  resolveParagraphOffset,
} from './dirtyReaderMapping';
import {
  acceptSuggestedPlan,
  bulkDeleteTargets,
  countUndecided,
  decisionAppliesTo,
} from './dirtyReaderSelection';
import { ConfirmDialog } from './ui/confirm-dialog';
import {
  FORMATTER_RULES,
  REVIEW_KIND_ORDER,
  formatterItemLabel,
  pluralLabel,
} from '../../shared/formatterRules';
import { nextSelection } from '../lib/listSelection';
import '../editor-chrome/editor.css';
import './DirtyReviewDialog.css';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

/** One review decision, exactly as `writeFormatReport` consumes it
    (`DirtyFixDecisionMap`'s value type). `merge`/`replace` added
    formatting-artifacts R1 for the unheadered "Merge into previous
    paragraph" verb and the terminator/pre-body-remnant "Replace with
    standard" verb. */
export type DirtyDecision = {
  action: 'fix' | 'dismiss' | 'remove' | 'keep' | 'merge' | 'replace' | 'strip-level' | 'flag';
  heading?: string;
};

export type DirtyFileResult = {
  /** Original input path — the key `formatDocuments`'s `perFile` and the
      scan/report both use. */
  file: string;
  /** Full path of the formatted OUTPUT .docx, when one exists — the
      read-only reopen's badge passes this. Absent for the LIVE pre-format
      review, which reads `file` (the original) instead. */
  outputPath?: string;
  /** The tracked-changes gate's choice for this file, applied in memory
      before the bytes are read — LIVE review only; a read-only reopen never
      sets this (the saved output already has the choice baked in). */
  revisionAction?: 'accept' | 'reject';
  unheadered?: {
    total: number;
    samples: DirtyItem[];
    fixed?: { text: string; heading: string }[];
    /** Every level as the target forma prints it — what lets a decided
        paragraph re-render in its post-fix state without a re-format.
        Optional: a payload from an older engine build simply has none, and the
        reader falls back to tint-only decoration. */
    levelPreviews?: LevelPreview[];
  };
};

export type { DirtyItem };

const DIRTY_REVIEW_VISIBLE_CAP = 20;

/* ─── Item vocabulary ─── */

function itemKind(item: DirtyItem): DirtyItemKind {
  // Items written before 2026-08-22 carry no `kind` and are unheadered.
  return item.kind ?? 'unheadered';
}

/** File name only, for the tab strip and file header — the full path is
    still available as the element's `title` tooltip. Handles both path
    separators since imports can come from either OS. */
function baseName(filePath: string): string {
  const idx = Math.max(filePath.lastIndexOf('/'), filePath.lastIndexOf('\\'));
  return idx >= 0 ? filePath.slice(idx + 1) : filePath;
}

/** Plain words per kind — the ONE label table (src/shared/formatterRules.ts,
    D2 2026-10-13) the engine's report reads too, so the review panel and the
    printed report never disagree. */
function kindLabel(item: {
  kind: DirtyItemKind;
  tableAdjacent?: DirtyItem['tableAdjacent'];
  hasLevel?: boolean;
}): string {
  return formatterItemLabel(item);
}

/** What a multi-item selection is made of, in the bar: "3 with no heading
    level · 1 unwanted return". Counts, not a truncated list of texts — the
    user already sees the ringed paragraphs on the page. */
function selectionSummary(items: DirtyItem[]): string {
  return REVIEW_KIND_ORDER.map((kind) => ({
    kind,
    n: items.filter((i) => itemKind(i) === kind).length,
  }))
    .filter((g) => g.n > 0)
    .map((g) => `${g.n} ${pluralLabel(kindLabel({ kind: g.kind }), g.n)}`)
    .join(' · ');
}

/** What the user reads in the decision bar and the flat list. */
function itemSnippet(item: DirtyItem): string {
  const kind = itemKind(item);
  if (item.text) return item.text;
  if (kind === 'empty-paragraph') {
    if (item.tableAdjacent && item.hasLevel) {
      return "Numbered blank line next to a table — it may be the table's spacing";
    }
    return 'Blank return between body paragraphs';
  }
  if (kind === 'page-break') return 'Page break between body paragraphs';
  return '(numbering only, no text)';
}

function decisionBadge(decision?: DirtyDecision) {
  if (decision?.action === 'fix' && decision.heading) {
    return (
      <StatusBadge status="dirty-fixed" label={`Set → ${decision.heading}`} variant="success" />
    );
  }
  if (decision?.action === 'merge') {
    return <StatusBadge status="dirty-merged" label="Merged into previous" variant="success" />;
  }
  if (decision?.action === 'replace') {
    return <StatusBadge status="dirty-replaced" label="Replaced with standard" variant="success" />;
  }
  if (decision?.action === 'strip-level') {
    return <StatusBadge status="dirty-fixed" label="Level removed" variant="success" />;
  }
  if (decision?.action === 'remove') {
    return <StatusBadge status="dirty-removed" label="Delete" variant="danger" />;
  }
  if (decision?.action === 'flag') {
    return <StatusBadge status="dirty-flagged" label="Flagged for editing" variant="warning" />;
  }
  if (decision?.action === 'keep' || decision?.action === 'dismiss') {
    return <StatusBadge status="dirty-kept" label="Kept" variant="neutral" />;
  }
  return <StatusBadge status="dirty-unresolved" label="Unresolved" variant="warning" />;
}

/** "Keep all remaining" — every undecided item takes an explicit Keep
    decision, recorded in the report exactly like a hand-picked one. Never a
    silent no-op: the badges above visibly flip to "Kept" for every item it
    touches, and the report lists each one as "Kept" rather than
    letting anything read as Unresolved by omission. */
function keepDecisionFor(): DirtyDecision {
  return { action: 'keep' };
}

/* ─── Fallback: the flat flagged-item list ─────────────────────────────── */

/** Pre-reader review UI, kept as the fallback whenever the document itself
    can't be rendered (web build, unreadable file). Every decision the reader
    offers is reachable here too — nothing is reader-only. */
export function DirtyFileGroup({
  file,
  unheadered,
  decisions,
  readOnly,
  onDecide,
}: {
  file: string;
  unheadered: NonNullable<DirtyFileResult['unheadered']>;
  decisions: Record<string, DirtyDecision>;
  readOnly: boolean;
  onDecide: (id: string, decision: DirtyDecision) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const fixed = unheadered.fixed || [];
  const allSamples = unheadered.samples || [];
  // Every item is reviewable (D6, 2026-10-13) — the engine decides nothing.
  const flagged = allSamples;
  const visible = expanded ? flagged : flagged.slice(0, DIRTY_REVIEW_VISIBLE_CAP);
  const hiddenCount = flagged.length - visible.length;

  return (
    <div style={{ marginBottom: 'var(--space-5)' }}>
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-bold)',
          marginBottom: 'var(--space-2)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          overflowWrap: 'anywhere',
        }}
      >
        <Icon
          icon={FileText}
          em={1.0769}
          aria-hidden="true"
          style={{ opacity: 0.7, flexShrink: 0 }}
        />
        {file}
      </div>
      {fixed.length > 0 && <AutoFixedList fixed={fixed} />}
      {visible.map((item, i) => {
        const decision = item.id ? decisions[item.id] : undefined;
        const kind = itemKind(item);
        return (
          <div
            key={item.id ?? i}
            style={{
              padding: 'var(--space-3)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-2)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                fontFamily: 'var(--font-mono, monospace)',
                marginBottom: 'var(--space-1)',
              }}
            >
              {kindLabel({ kind, tableAdjacent: item.tableAdjacent, hasLevel: item.hasLevel })} · [
              {item.style}]
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                marginBottom: 'var(--space-2)',
                overflowWrap: 'anywhere',
                fontStyle: item.text ? undefined : 'italic',
                color: item.text ? undefined : 'var(--color-text-muted)',
              }}
            >
              {itemSnippet(item)}
            </div>
            {readOnly ? (
              decisionBadge(decision)
            ) : (
              <ItemActions
                items={[item]}
                decisions={decisions}
                onDecide={(d) => item.id && onDecide(item.id, d)}
              />
            )}
          </div>
        );
      })}
      {hiddenCount > 0 && (
        <Button variant="outline" size="sm" onClick={() => setExpanded(true)}>
          Show {hiddenCount} more
        </Button>
      )}
      {fixed.length === 0 && flagged.length === 0 && (
        <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-muted)' }}>
          Nothing flagged for this file.
        </div>
      )}
    </div>
  );
}

/** The Formatter's rules, rendered from the ONE table (D2, 2026-10-13):
    what it rewrites on its own (only its own earlier output) vs what it
    shows you to decide (kept unless you choose otherwise). */
function FormatterRulesLegend() {
  const certain = FORMATTER_RULES.filter((r) => r.category === 'certain');
  const facetActions = FORMATTER_RULES.filter((r) => r.category === 'facet-action');
  const judgment = FORMATTER_RULES.filter((r) => r.category === 'judgment');
  const heading: React.CSSProperties = {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-muted)',
    marginBottom: 'var(--space-1)',
  };
  const list = (rules: typeof FORMATTER_RULES) => (
    <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
      {rules.map((r) => (
        <li
          key={r.kind}
          style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-1)' }}
        >
          <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>{r.label}</span>
          <span style={{ color: 'var(--color-text-muted)' }}> — {r.whatHappens}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <section
      aria-label="Formatter rules"
      style={{
        marginBottom: 'var(--space-3)',
        padding: 'var(--space-3)',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div>
        <div style={heading}>Certain — only SpecForma’s own earlier output</div>
        {list(certain)}
      </div>
      <div>
        <div style={heading}>Facet actions — each part you checked does its own zone</div>
        {list(facetActions)}
      </div>
      <div>
        <div style={heading}>Your call — kept unless you decide otherwise</div>
        {list(judgment)}
      </div>
    </section>
  );
}

function AutoFixedList({ fixed }: { fixed: { text: string; heading: string }[] }) {
  const total = fixed.length;
  return (
    <div
      style={{
        marginBottom: 'var(--space-3)',
        padding: 'var(--space-2)',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-success)',
          textTransform: 'uppercase',
          letterSpacing: '.04em',
          marginBottom: 'var(--space-1)',
        }}
      >
        Auto-fixed ({total}) — no action needed
      </div>
      {fixed.map((f, i) => (
        <div
          key={i}
          style={{
            fontSize: 'var(--font-size-base)',
            padding: `${rem(2)} 0`,
            overflowWrap: 'anywhere',
          }}
        >
          <span
            style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--color-success)' }}
          >
            [→ {f.heading}]
          </span>{' '}
          <span style={{ color: 'var(--color-text-muted)' }}>{f.text}</span>
        </div>
      ))}
    </div>
  );
}

/** Visible twin of `aria-keyshortcuts="Enter"` (2026-09-08) — every
    primary/default button in the bar shows this chip so Enter reads as a
    visible affordance, not a hidden trick only power users discover. Sits
    INSIDE the button, right of the label; tokens only. */
function EnterChip() {
  return (
    <kbd className="sf-kbd-chip" aria-hidden="true">
      Enter
    </kbd>
  );
}

/** Which verb-row a kind belongs to. Keep is universal and rendered inside
    every row, so it is not its own category. */
type ItemCategory = 'heading' | 'typed' | 'block' | 'terminator';
function categoryOf(kind: DirtyItemKind): ItemCategory {
  if (kind === 'unheadered') return 'heading';
  // Sync C 7.1: a hand-typed list marker — Keep (primary) · level 1–9 · Flag.
  if (kind === 'typed-list-marker') return 'typed';
  // A pre-body "SECTION …" table (R5) is decided like a blank: Keep or Delete.
  if (kind === 'empty-paragraph' || kind === 'page-break' || kind === 'legacy-title-table')
    return 'block';
  return 'terminator'; // stale-terminator, pre-body-remnant
}

/** The decision controls for a SELECTION of items — shared by the floating bar
    and the fallback list (which always passes exactly one) so the two can
    never drift apart.

    A decision fans out only to the items whose category accepts it: Set
    level / Merge into previous to unheadered paragraphs, Delete to blanks
    and breaks, Replace with standard to nonstandard terminators and leftover
    pre-body lines. Keep belongs to every category. A mixed selection shows
    one row PER category present, each labelled with the subset it acts on —
    no button is ever silently a no-op. */
function ItemActions({
  items,
  decisions,
  onDecide,
  onSkip,
}: {
  items: DirtyItem[];
  decisions: Record<string, DirtyDecision>;
  onDecide: (d: DirtyDecision) => void;
  onSkip?: () => void;
}) {
  const headingItems = items.filter((i) => categoryOf(itemKind(i)) === 'heading');
  const typedItems = items.filter((i) => categoryOf(itemKind(i)) === 'typed');
  const blockItems = items.filter((i) => categoryOf(itemKind(i)) === 'block');
  const terminatorItems = items.filter((i) => categoryOf(itemKind(i)) === 'terminator');
  const deletableTerminatorItems = items.filter(
    (i) => itemKind(i) === 'stale-terminator' || itemKind(i) === 'pre-body-remnant'
  );
  const groupCount = [headingItems, typedItems, blockItems, terminatorItems].filter(
    (g) => g.length > 0
  ).length;
  const mixed = groupCount > 1;
  const single = items.length === 1 ? items[0] : undefined;
  const decisionOf = (item: DirtyItem) => (item.id ? decisions[item.id] : undefined);

  /** Pressed only when EVERY item the button would act on already carries
      that decision — a half-decided selection reads as undecided. */
  const allHave = (subset: DirtyItem[], test: (d?: DirtyDecision) => boolean) =>
    subset.length > 0 && subset.every((i) => test(decisionOf(i)));

  const row: React.CSSProperties = {
    display: 'flex',
    gap: 'var(--space-1)',
    flexWrap: 'wrap',
    alignItems: 'center',
  };
  const groupLabel: React.CSSProperties = {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-muted)',
    marginRight: 'var(--space-1)',
  };

  const skipBtn = onSkip ? (
    <Button type="button" variant="ghost" size="sm" onClick={onSkip}>
      Skip
    </Button>
  ) : null;

  const keepBtn = (subset: DirtyItem[]) => (
    <Button
      type="button"
      variant={
        allHave(subset, (d) => d?.action === 'keep' || d?.action === 'dismiss')
          ? 'secondary'
          : 'outline'
      }
      size="sm"
      aria-pressed={allHave(subset, (d) => d?.action === 'keep' || d?.action === 'dismiss')}
      aria-keyshortcuts="K"
      onClick={() => onDecide({ action: 'keep' })}
    >
      Keep
    </Button>
  );

  // Flag for editing (R2, 2026-09-08) — available on EVERY kind, never the
  // primary verb. The formatter never touches a spec's CONTENT; this is the
  // reviewer's "fix this by hand in Word" call. Never the primary/default —
  // Enter keeps its existing meaning (Accept/whatever verb is suggested).
  const flagBtn = (subset: DirtyItem[]) => (
    <Button
      type="button"
      variant={allHave(subset, (d) => d?.action === 'flag') ? 'secondary' : 'outline'}
      size="sm"
      aria-pressed={allHave(subset, (d) => d?.action === 'flag')}
      aria-keyshortcuts="F"
      onClick={() => onDecide({ action: 'flag' })}
    >
      <Icon icon={Flag} size="md" aria-hidden="true" />
      Flag for editing
    </Button>
  );

  // Legend (2026-09-08) — only the parts that apply to the selection
  // present. K is universal (Keep is always offered); Enter accepts
  // whatever the current selection's suggestion is.
  const legendParts: string[] = [];
  if (headingItems.length > 0 || typedItems.length > 0) legendParts.push('1–9 level');
  legendParts.push('Enter accept');
  if (blockItems.length > 0 || deletableTerminatorItems.length > 0)
    legendParts.push('Delete key deletes');
  legendParts.push('K keep');
  legendParts.push('F flag');
  if (onSkip) legendParts.push('S skip');
  const legendRow = (
    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
      {legendParts.join(' · ')}
    </div>
  );

  const levelButtons = (subset: DirtyItem[]) =>
    Array.from({ length: 9 }, (_, i) => i + 1).map((n) => {
      const heading = `Heading${n}`;
      const isSuggested = single?.suggestedLevel === n;
      const isChosen = allHave(subset, (d) => d?.action === 'fix' && d.heading === heading);
      return (
        <Button
          key={n}
          type="button"
          variant={isChosen ? 'default' : 'outline'}
          size="sm"
          aria-pressed={isChosen}
          aria-keyshortcuts={String(n)}
          aria-label={`Set ${subset.length > 1 ? `${subset.length} paragraphs` : ''} to Heading ${n}${isSuggested ? ' (suggested)' : ''}`.replace(
            /\s+/g,
            ' '
          )}
          style={
            isSuggested && !isChosen
              ? { borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }
              : undefined
          }
          onClick={() => onDecide({ action: 'fix', heading })}
        >
          H{n}
        </Button>
      );
    });

  const headingGroup = headingItems.length > 0 && (
    <div style={row}>
      {mixed && <span style={groupLabel}>{headingItems.length} with no heading level</span>}
      {levelButtons(headingItems)}
      <Button
        type="button"
        variant={allHave(headingItems, (d) => d?.action === 'merge') ? 'default' : 'secondary'}
        size="sm"
        aria-pressed={allHave(headingItems, (d) => d?.action === 'merge')}
        onClick={() => onDecide({ action: 'merge' })}
      >
        Merge into previous
      </Button>
      {keepBtn(headingItems)}
      {!mixed && flagBtn(headingItems)}
      {!mixed && skipBtn}
      {/* The old "Suggested: H4" chip is gone (2026-08-23): the Accept button
          in the bar's fixed slot now CARRIES the level, so repeating it here
          would be a second voice saying the same thing. The tinted H button
          above still shows WHICH level is suggested, for deviating. */}
    </div>
  );

  // Sync C 7.1 (Josh 2026-10-20): a hand-typed marker ("A. Smith…") is a
  // question, not a restyle. Keep is the primary verb (Enter keeps); a level
  // button strips the typed marker and numbers the paragraph; Flag hands it
  // to Word. No Delete, no Merge for this kind.
  const typedKept = allHave(typedItems, (d) => d?.action === 'keep' || d?.action === 'dismiss');
  const typedGroup = typedItems.length > 0 && (
    <div style={row}>
      {mixed && (
        <span style={groupLabel}>
          {typedItems.length} typed list {typedItems.length === 1 ? 'marker' : 'markers'}
        </span>
      )}
      <Button
        type="button"
        variant={mixed ? (typedKept ? 'secondary' : 'outline') : 'default'}
        size="sm"
        aria-pressed={typedKept}
        aria-keyshortcuts={mixed ? 'K' : 'Enter K'}
        title={mixed ? undefined : 'Enter'}
        onClick={() => onDecide({ action: 'keep' })}
      >
        Keep
        {!mixed && <EnterChip />}
      </Button>
      {levelButtons(typedItems)}
      {!mixed && flagBtn(typedItems)}
      {!mixed && skipBtn}
    </div>
  );

  // Table-adjacent blank lines with a level (2026-09-08): Remove level keeps
  // the line and drops its number — offered, never the default.
  const blockHasLevel = blockItems.some((i) => i.hasLevel);
  const blockKept = allHave(blockItems, (d) => d?.action === 'keep' || d?.action === 'dismiss');

  // ⛔ SPEC_WORKFLOW §13 (D6, 2026-10-13): Keep is the primary verb for blank
  // returns and page breaks — Enter keeps. Delete is the reviewer's explicit
  // choice: danger styling, pushed to the far end of the row so it is never
  // next to the primary.
  const blockGroup = blockItems.length > 0 && (
    <div style={row}>
      {mixed && (
        <span style={groupLabel}>
          {blockItems.length} blank {blockItems.length === 1 ? 'or break' : 'or breaks'}
        </span>
      )}
      <Button
        type="button"
        variant={mixed ? (blockKept ? 'secondary' : 'outline') : 'default'}
        size="sm"
        aria-pressed={blockKept}
        aria-keyshortcuts={mixed ? 'K' : 'Enter K'}
        title={mixed ? undefined : 'Enter'}
        onClick={() => onDecide({ action: 'keep' })}
      >
        Keep
        {!mixed && <EnterChip />}
      </Button>
      {blockHasLevel && (
        <Button
          type="button"
          variant={
            allHave(blockItems, (d) => d?.action === 'strip-level') ? 'secondary' : 'outline'
          }
          size="sm"
          aria-pressed={allHave(blockItems, (d) => d?.action === 'strip-level')}
          aria-label="Remove level — keeps the blank line, drops its number"
          onClick={() => onDecide({ action: 'strip-level' })}
        >
          Remove level
        </Button>
      )}
      {!mixed && flagBtn(blockItems)}
      {!mixed && skipBtn}
      <Button
        type="button"
        variant="destructive"
        size="sm"
        aria-pressed={allHave(blockItems, (d) => d?.action === 'remove')}
        aria-keyshortcuts="Delete"
        style={{ marginLeft: 'auto' }}
        onClick={() => onDecide({ action: 'remove' })}
      >
        Delete
      </Button>
    </div>
  );

  const terminatorGroup = terminatorItems.length > 0 && (
    <div style={row}>
      {mixed && (
        <span style={groupLabel}>
          {terminatorItems.length} nonstandard {terminatorItems.length === 1 ? 'item' : 'items'}
        </span>
      )}
      {/* Replace with standard is this kind's suggested verb, so it
          stands in for the Accept slot and carries the primary styling. */}
      <Button
        type="button"
        variant="default"
        size="sm"
        aria-pressed={allHave(terminatorItems, (d) => d?.action === 'replace')}
        aria-keyshortcuts="Enter"
        onClick={() => onDecide({ action: 'replace' })}
      >
        Replace with standard
        <EnterChip />
      </Button>
      {keepBtn(terminatorItems)}
      {!mixed && flagBtn(terminatorItems)}
      {!mixed && skipBtn}
      {/* SPEC_WORKFLOW §13: a mid-body END OF SECTION may be deleted, but
          only as the reviewer's explicit choice — danger, far end of the row. */}
      {deletableTerminatorItems.length > 0 && (
        <Button
          type="button"
          variant="destructive"
          size="sm"
          aria-pressed={allHave(deletableTerminatorItems, (d) => d?.action === 'remove')}
          aria-keyshortcuts="Delete"
          style={{ marginLeft: 'auto' }}
          onClick={() => onDecide({ action: 'remove' })}
        >
          Delete
        </Button>
      )}
    </div>
  );

  if (!mixed) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {headingGroup || typedGroup || blockGroup || terminatorGroup}
        {legendRow}
      </div>
    );
  }

  // Mixed selection: Flag fans out across every kind (it applies to all of
  // them), so it earns ONE shared button here rather than being repeated in
  // every group's row — unlike Keep, which stays per-group above.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      {headingGroup}
      {typedGroup}
      {blockGroup}
      {terminatorGroup}
      <div style={row}>
        {flagBtn(items)}
        {skipBtn}
      </div>
      {legendRow}
    </div>
  );
}

/**
 * The bar's fixed action slot (2026-08-23).
 *
 * ⛔ THE SPAM LAW (Josh): this control's screen position must NEVER move. He
 * reviews 268 items by clicking the same pixel over and over, and the engine's
 * suggestion is now nearly always right — so the slot is a fixed-size box,
 * pinned to the bar's bottom-left, and it renders EXACTLY one of two things at
 * exactly one size: the accept button, or a disabled placeholder. An item with
 * no suggestion therefore eats a spam-click as a no-op; it can never hand that
 * click to a different action.
 */
function AcceptSlot({
  label,
  disabled,
  onAccept,
}: {
  label: string;
  disabled: boolean;
  onAccept: () => void;
}) {
  return (
    <div className="sf-accept-slot">
      <Button
        type="button"
        data-sf-accept=""
        variant={disabled ? 'outline' : 'default'}
        disabled={disabled}
        aria-keyshortcuts="Enter"
        aria-label={disabled ? 'No suggestion for this item' : `${label} (Enter)`}
        title={disabled ? undefined : 'Enter'}
        onClick={onAccept}
        style={{ width: '100%' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        {!disabled && <EnterChip />}
      </Button>
    </div>
  );
}

/* ─── Reader zoom (T8, DESIGN.md §3.6) ───
   The reader is a design surface: its dial only seeds the STARTING zoom, so
   the value this component owns is RELATIVE to that dial, never the dial
   itself. D1 cross-surface parity (DESIGN.md §4 "Per-user persistence"
   lists zoom explicitly): cloud `user_prefs` + localStorage fast cache —
   this used to be per-device only. */
const READER_ZOOM_KEY = 'sf-reader-zoom';
const READER_ZOOM_MIN = 0.5;
const READER_ZOOM_MAX = 1.8;
const READER_ZOOM_STEP = 0.05;

function clampReaderZoom(n: number): number {
  return Math.max(READER_ZOOM_MIN, Math.min(READER_ZOOM_MAX, n));
}

function parseReaderZoom(raw: string): number {
  const n = parseFloat(raw);
  return Number.isFinite(n) ? clampReaderZoom(n) : 1;
}

/** Native `scrollIntoView` is unreliable on a descendant of a transformed
    (scaled) ancestor, so artifact navigation computes the scroll position
    itself. `getBoundingClientRect()` already reflects the CSS
    `transform: scale()` on the reader's zoom wrapper — no manual dial/zoom
    multiplication needed — so this is just container-relative arithmetic,
    landing the highlight centred in the viewport at any zoom — the same
    `block: 'center'` landing the native call gave before the zoom wrapper. */
function scrollIntoViewCentered(container: HTMLElement, el: HTMLElement, smooth: boolean): void {
  const cRect = container.getBoundingClientRect();
  const eRect = el.getBoundingClientRect();
  const targetTop = Math.max(0, (cRect.height - eRect.height) / 2);
  const nextScrollTop = Math.max(0, container.scrollTop + (eRect.top - cRect.top) - targetTop);
  let nextScrollLeft = container.scrollLeft;
  if (eRect.left < cRect.left || eRect.right > cRect.right) {
    nextScrollLeft = Math.max(
      0,
      container.scrollLeft + (eRect.left - cRect.left) - (cRect.width - eRect.width) / 2
    );
  }
  container.scrollTo({
    top: nextScrollTop,
    left: nextScrollLeft,
    behavior: smooth ? 'smooth' : 'auto',
  });
}

/* ─── Reader state ─── */

type ReaderState =
  | { status: 'loading' }
  | { status: 'ready'; renderId: number }
  | { status: 'error'; message: string };

export function DirtyReviewDialog({
  open,
  readOnly,
  files,
  decisions,
  onDecide,
  onKeepAllRemaining,
  onClose,
}: {
  open: boolean;
  readOnly: boolean;
  files: DirtyFileResult[];
  decisions: Record<string, Record<string, DirtyDecision>>;
  onDecide: (file: string, id: string, decision: DirtyDecision) => void;
  onKeepAllRemaining: () => void;
  /** Done, Esc, or backdrop dismiss — all three routes here (DESIGN.md §7:
      overlays always close on Esc). The caller decides whether that is safe
      to honor immediately (nothing left undecided) or must first confirm
      shipping with unresolved artifacts (T7 "no silent shipping"); this
      component never makes that call itself. */
  onClose: () => void;
}) {
  // Stable across renders so the auto-advance effect below can call the
  // LATEST onClose without listing it as a dependency (the parent passes a
  // fresh closure every render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const filesKey = files.map((f) => f.file).join('|');
  const [activeFile, setActiveFile] = useState('');
  const [reader, setReader] = useState<ReaderState>({ status: 'loading' });
  const [cursor, setCursor] = useState(0);
  const [showAutoFixed, setShowAutoFixed] = useState(false);
  const [showRules, setShowRules] = useState(false);
  /** "Delete all blank returns" / "Delete all page breaks" confirm (D6). */
  const [bulkDeleteKind, setBulkDeleteKind] = useState<'empty-paragraph' | 'page-break' | null>(
    null
  );
  /** Flagged-item indices the decision bar acts on. Empty ⇒ "just the
      cursor" — the selection is derived below so the two can never disagree. */
  const [selection, setSelection] = useState<number[]>([]);
  const [anchor, setAnchor] = useState<number | null>(null);
  /** Relative to the surface's own dial-seeded start (DESIGN.md §3.6) —
      1.0 means "actual size for this device", not "100% of the window". */
  const [readerZoom, setReaderZoom] = usePref<number>(READER_ZOOM_KEY, READER_ZOOM_KEY, 1, {
    parse: parseReaderZoom,
    serialize: String,
  });
  const [dial, setDial] = useState<number>(() => getUiScale().dial);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const docRef = useRef<HTMLDivElement | null>(null);
  const styleRef = useRef<HTMLDivElement | null>(null);
  /** base64 per file — switching tabs must never re-hit the disk. */
  const bytesCache = useRef<Record<string, string>>({});
  /** Rendered <p> elements of the ACTIVE document, in document order. */
  const paraElsRef = useRef<HTMLElement[]>([]);
  /** Their text, same order — cached alongside so per-item verification
      (itemRenderedIndex) never re-reads textContent on every paint. */
  const paraTextsRef = useRef<string[]>([]);
  /** How many rendered paragraphs sit above the body content start. */
  const offsetRef = useRef<number | null>(null);
  const decoratedRef = useRef<HTMLElement[]>([]);
  /** Set by a click: the paragraph is already under the pointer, so the
      snap-to-centre scroll must not yank the page out from under it. */
  const skipNextSnapRef = useRef(false);

  // Ctrl+wheel steps the reader's own zoom — a native, non-passive listener,
  // since React's synthetic onWheel is passive and can't stop the browser's
  // own page-zoom (same pattern as StudioCanvas's wheelZoomRef).
  const onReaderWheel = useCallback(
    (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setReaderZoom((z) =>
        clampReaderZoom(z + (e.deltaY < 0 ? READER_ZOOM_STEP : -READER_ZOOM_STEP))
      );
    },
    [setReaderZoom]
  );

  /** Merges the scroll container ref with that native wheel listener. */
  const readerNodeRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollRef.current = el;
      if (!el) return;
      el.addEventListener('wheel', onReaderWheel, { passive: false });
    },
    [onReaderWheel]
  );

  const activeResult = files.find((f) => f.file === activeFile);
  const outputPath = activeResult?.outputPath;
  // Live pre-format review has no output yet — it reads the ORIGINAL input,
  // with the tracked-changes gate's choice applied in memory so the scan's
  // item ids still line up. A read-only reopen renders the saved OUTPUT
  // instead, with no revision action (it is already baked in).
  const readPath = outputPath || activeResult?.file;
  const readRevisionAction = outputPath ? undefined : activeResult?.revisionAction;
  const autoFixed = useMemo(() => activeResult?.unheadered?.fixed || [], [activeResult]);
  const autoFixedCount = autoFixed.length;
  const levelPreviews = activeResult?.unheadered?.levelPreviews;
  const fileDecisions = useMemo(() => decisions[activeFile] || {}, [decisions, activeFile]);

  /** Every item with an id is navigable — an id is what applyDirtyFixes and
      the report both key on. Nothing is engine-decided (D6, 2026-10-13). */
  const items = useMemo(
    () => (activeResult?.unheadered?.samples || []).filter((s) => !!s.id),
    [activeResult]
  );
  const itemsRef = useRef<DirtyItem[]>(items);
  itemsRef.current = items;

  const totalFlagged = files.reduce((n, f) => n + (f.unheadered?.samples?.length || 0), 0);
  const totalUndecided = files.reduce(
    (n, f) => n + countUndecided(f.unheadered?.samples || [], decisions[f.file] || {}),
    0
  );
  /** Count of items decided "Flag for editing" (R2, 2026-09-08) across every
      file — footer-only, plain count, never confused with `totalFlagged`
      above (that name predates this verb and means "total items flagged BY
      THE SCAN", not "flagged for editing by the reviewer"). */
  const totalFlaggedForEditing = files.reduce((n, f) => {
    const fDecisions = decisions[f.file] || {};
    const samples = f.unheadered?.samples || [];
    return n + samples.filter((s) => s.id && fDecisions[s.id]?.action === 'flag').length;
  }, 0);
  /** Undecided blank returns / page breaks across every file — what the
      explicit bulk-delete buttons would mark (D6). */
  const bulkTargets = (kind: 'empty-paragraph' | 'page-break') =>
    files.map((f) => ({
      file: f.file,
      ids: bulkDeleteTargets(f.unheadered?.samples || [], decisions[f.file] || {}, kind),
    }));
  const bulkCount = (kind: 'empty-paragraph' | 'page-break') =>
    bulkTargets(kind).reduce((n, t) => n + t.ids.length, 0);
  const blankReturnsLeft = bulkCount('empty-paragraph');
  const pageBreaksLeft = bulkCount('page-break');
  const confirmBulkDelete = () => {
    if (!bulkDeleteKind) return;
    for (const t of bulkTargets(bulkDeleteKind)) {
      for (const id of t.ids) onDecide(t.file, id, { action: 'remove' });
    }
    setBulkDeleteKind(null);
  };

  /** This file's own undecided count — drives the T6 auto-advance below and
      the per-file tab badge. */
  const currentFileUndecided = countUndecided(
    activeResult?.unheadered?.samples || [],
    fileDecisions
  );
  /** True once the active file HAD something undecided, since the last file
      switch — so auto-advance fires only when a decision just cleared the
      file, never on landing on an already-finished one. */
  const hadUndecidedRef = useRef(false);

  // Open ⇒ land on the first file. Close ⇒ drop every cache so the next open
  // reads the documents fresh (fixes may have been written since).
  useEffect(() => {
    if (!open) {
      bytesCache.current = {};
      paraElsRef.current = [];
      paraTextsRef.current = [];
      decoratedRef.current = [];
      offsetRef.current = null;
      setActiveFile('');
      setCursor(0);
      setSelection([]);
      setAnchor(null);
      setShowAutoFixed(false);
      setShowRules(false);
      setBulkDeleteKind(null);
      return;
    }
    setActiveFile((prev) => (files.some((f) => f.file === prev) ? prev : files[0]?.file || ''));
    // `filesKey` stands in for `files` on purpose — the prop is a fresh array
    // on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filesKey]);

  useEffect(() => {
    setCursor(0);
    setSelection([]);
    setAnchor(null);
    paraElsRef.current = [];
    paraTextsRef.current = [];
    decoratedRef.current = [];
    offsetRef.current = null;
    hadUndecidedRef.current = false;
  }, [activeFile]);

  // T6 auto-advance: once a decision clears the LAST undecided item in the
  // active file, jump to the next file that still has undecided items. When
  // none remain anywhere, the review is done — close automatically, exactly
  // as if Done had been clicked, so a full pass never needs an extra click.
  // Landing on an already-finished file (tab click, file switch) never
  // triggers this — only an in-file decision that brings the count to zero.
  useEffect(() => {
    if (readOnly) return;
    if (currentFileUndecided > 0) {
      hadUndecidedRef.current = true;
      return;
    }
    if (!hadUndecidedRef.current) return;
    hadUndecidedRef.current = false;
    const nextFile = files.find((f) => {
      if (f.file === activeFile) return false;
      return countUndecided(f.unheadered?.samples || [], decisions[f.file] || {}) > 0;
    });
    if (nextFile) setActiveFile(nextFile.file);
    else onCloseRef.current();
  }, [currentFileUndecided, activeFile, files, decisions, readOnly]);

  // The dial only seeds where THIS surface starts; a live dial change (window
  // resize) recomputes the transform without touching the relative value.
  useEffect(() => subscribeUiScale((v) => setDial(v.dial)), []);

  // Read the .docx and hand it to docx-preview. The container is mounted at
  // all times (the loading and error states overlay it) so the ref is live
  // before the first render pass.
  useEffect(() => {
    if (!open || !activeFile) return;
    let alive = true;
    setReader({ status: 'loading' });

    (async () => {
      try {
        let base64 = bytesCache.current[activeFile];
        if (!base64) {
          const api = (window as unknown as { api?: SpecFormaApi }).api;
          if (!readPath) throw new Error('the document’s location wasn’t recorded');
          if (!api?.readFormatterDocx) throw new Error('document reading isn’t available here');
          const res = await api.readFormatterDocx(readPath, readRevisionAction);
          base64 = res.base64;
          if (!alive) return;
          bytesCache.current[activeFile] = base64;
        }
        const container = docRef.current;
        if (!container) throw new Error('the reader surface never mounted');
        container.replaceChildren();
        styleRef.current?.replaceChildren();
        await renderAsync(
          base64ToArrayBuffer(base64),
          container,
          styleRef.current || undefined,
          READER_RENDER_OPTIONS
        );
        if (!alive) return;
        const paras = collectRenderedParagraphs(container);
        const texts = paras.map((p) => p.textContent || '');
        const levels = paras.map((p) => renderedParagraphLevel(p));
        paraElsRef.current = paras;
        paraTextsRef.current = texts;
        offsetRef.current = resolveParagraphOffset(texts, itemsRef.current, levels);
        setReader({ status: 'ready', renderId: Date.now() });
      } catch (err: unknown) {
        if (!alive) return;
        paraElsRef.current = [];
        paraTextsRef.current = [];
        offsetRef.current = null;
        setReader({
          status: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, activeFile, readPath, readRevisionAction]);

  const readerScale = readerZoom * dial;

  const navCount = items.length;
  const safeCursor = navCount === 0 ? 0 : Math.min(Math.max(cursor, 0), navCount - 1);
  const currentItem = navCount > 0 ? items[safeCursor] : undefined;

  /** The selection the decision bar acts on. Always contains the cursor, so
      "no explicit selection" and "one item selected" are the same thing to
      everything downstream. */
  const selectedIndices = useMemo(() => {
    if (navCount === 0) return [];
    const kept = selection.filter((i) => i >= 0 && i < navCount);
    return kept.length > 0 ? kept : [safeCursor];
  }, [selection, navCount, safeCursor]);
  const selectedItems = useMemo(
    () => selectedIndices.map((i) => items[i]).filter(Boolean),
    [selectedIndices, items]
  );

  /** The rendered node a flagged item lives on, or null when the mapping
      couldn't be established (stale document, unrenderable file) OR when
      THIS item's own text doesn't land where the file's offset says it
      should — a per-item degrade, so one bad join un-highlights only that
      item instead of the whole panel (table-adjacent dead-panel bug,
      2026-09-03). The item stays reachable and decidable either way; it just
      draws no ring. */
  const elementFor = useCallback((item: DirtyItem | undefined): HTMLElement | null => {
    if (!item) return null;
    const idx = itemRenderedIndex(item, paraTextsRef.current, offsetRef.current);
    return idx == null ? null : paraElsRef.current[idx] || null;
  }, []);

  // Paint the decorations: every flagged paragraph tinted by its decision, the
  // decided ones re-rendered in their POST-FIX state, the selected ones ringed
  // in primary, the current one ringed in focus, blanks and breaks carrying
  // their formatting mark.
  //
  // Everything here is a class, an attribute, or an inline style ON THE
  // RENDERED PARAGRAPH ITSELF — never a positioned overlay measured against
  // it. That is the whole alignment guarantee: a decoration that IS the
  // paragraph's own box cannot drift from it at any scroll, resize, or zoom.
  useEffect(() => {
    if (reader.status !== 'ready') return;
    for (const el of decoratedRef.current) {
      Array.from(el.classList).forEach((c) => {
        if (c.startsWith('sf-dirty')) el.classList.remove(c);
      });
      // Restores the paragraph's pristine inline style — undo and redecide
      // must always land back on what Word would actually show.
      clearPostFixPreview(el);
      el.removeAttribute('data-sf-item');
      el.removeAttribute('data-sf-selected');
      el.removeAttribute('tabindex');
      el.removeAttribute('aria-current');
    }
    const STATE_CLASSES = [
      'sf-dirty--pending',
      'sf-dirty--fixed',
      'sf-dirty--removed',
      'sf-dirty--flagged',
      'sf-dirty--settled',
    ];
    const decisionOf = (item: DirtyItem) => (item.id ? fileDecisions[item.id] : undefined);
    /** Where a flagged item sits in the rendered paragraph list — null for a
        per-item mismatch too, same rule as elementFor. */
    const renderedIndex = (item: DirtyItem): number | null => {
      return itemRenderedIndex(item, paraTextsRef.current, offsetRef.current);
    };

    /* Task 3 — the real sequence counter behind every post-fix preview.
       The document's own levels come from the rendered DOM; this session's
       decisions are layered on top (a decided paragraph now takes a number, a
       removed one stops taking one), and Word's counter rules run over the
       result. Everything above the body content start is excluded — a
       pre-body title block never numbers anything.

       formatting-artifacts R1 (2026-09-03): this only means anything on a
       FORMATTED output — its numbering classes (`docx-num-…`) came from the
       forma's own numbering pass. The live pre-format review renders the
       ORIGINAL, unformatted document instead: it may carry no numbering
       classes at all (that is often exactly WHY a paragraph is flagged
       unheadered), so replaying "Word's counter rules" over it would invent
       numbers with no basis. Degrade gracefully instead — skip the replay
       entirely and let every post-fix preview fall back to the engine's own
       `levelPreviews` sample (counter 1's shape), never a guess. */
    const bodyStart = offsetRef.current ?? 0;
    const counterSnapshots: Map<number, number[]> = outputPath
      ? (() => {
          const levels: (number | null)[] = paraElsRef.current.map((el, i) =>
            i < bodyStart ? null : renderedParagraphLevel(el)
          );
          const wanted = new Set<number>();
          for (const item of items) {
            const idx = renderedIndex(item);
            if (idx == null || idx >= levels.length) continue;
            const d = decisionOf(item);
            if (d?.action === 'fix') {
              levels[idx] = headingLevelNumber(d.heading);
              wanted.add(idx);
            } else if (d?.action === 'remove' && itemKind(item) !== 'page-break') {
              levels[idx] = null;
            }
          }
          return outlineCounters(levels, wanted);
        })()
      : new Map();

    const stateClass = (item: DirtyItem) => {
      const d = decisionOf(item);
      if (d?.action === 'fix') return 'sf-dirty--fixed';
      if (d?.action === 'remove') return 'sf-dirty--removed';
      if (d?.action === 'flag') return 'sf-dirty--flagged';
      if (d) return 'sf-dirty--settled';
      return 'sf-dirty--pending';
    };
    /** The decided paragraph, drawn as the export will print it. */
    const paintPostFix = (item: DirtyItem) => {
      const el = elementFor(item);
      if (!el) return;
      const d = decisionOf(item);
      if (d?.action === 'remove') {
        // Removing a page break that rides a real paragraph deletes the BREAK,
        // not the words — so only the break is drawn as going away.
        applyRemovedPreview(
          el,
          itemKind(item) === 'page-break' && item.text ? 'break' : 'paragraph'
        );
        return;
      }
      if (d?.action !== 'fix') return;
      const preview = postFixPreview(d.heading, levelPreviews);
      if (!preview) return;
      // The forma's sample number is this level at counter 1; print it at the
      // counter this paragraph actually lands on.
      const level = headingLevelNumber(d.heading);
      const idx = renderedIndex(item);
      const counters = idx == null ? undefined : counterSnapshots.get(idx);
      applyPostFixPreview(
        el,
        level != null && counters
          ? { ...preview, prefix: numberFromSample(preview.prefix, level, counters) }
          : preview
      );
    };

    const selected = new Set(selectedIndices);
    const touched: HTMLElement[] = [];
    items.forEach((item, index) => {
      const el = elementFor(item);
      if (!el) return;
      const kind = itemKind(item);
      el.classList.add('sf-dirty', stateClass(item));
      if (kind === 'empty-paragraph') el.classList.add('sf-dirty--empty');
      if (kind === 'page-break') el.classList.add('sf-dirty--break');
      if (!el.hasAttribute('data-sf-item')) el.setAttribute('data-sf-item', String(index));
      el.setAttribute('tabindex', '-1');
      // A hanging first line is painted outside the paragraph's own box; pull
      // the box out to meet it before anything tints it.
      encloseHangingIndent(el);
      if (selected.has(index)) {
        el.classList.add('sf-dirty--selected');
        el.setAttribute('data-sf-selected', '');
      }
      touched.push(el);
    });
    items.forEach(paintPostFix);
    // One paragraph can carry TWO items — a manual page break riding on an
    // unstyled paragraph is flagged twice, once per kind. Repaint the current
    // item LAST and alone, so the tint under the ring always matches the
    // decision the bar is offering rather than whichever item painted last.
    const currentEl = elementFor(items[safeCursor]);
    if (currentEl) {
      currentEl.classList.remove(...STATE_CLASSES);
      currentEl.classList.add('sf-dirty--current', stateClass(items[safeCursor]));
      currentEl.setAttribute('data-sf-item', String(safeCursor));
      currentEl.setAttribute('aria-current', 'true');
      paintPostFix(items[safeCursor]);
    }
    decoratedRef.current = touched;
  }, [
    reader,
    items,
    fileDecisions,
    safeCursor,
    selectedIndices,
    levelPreviews,
    elementFor,
    outputPath,
  ]);

  // Snap the current item to the middle of the reader and take focus with it,
  // so the keyboard and the screen reader follow the eye. A click is exempt:
  // the paragraph is already under the pointer, and yanking the page mid
  // Ctrl-click would make multi-select unusable.
  useEffect(() => {
    if (reader.status !== 'ready') return;
    const el = elementFor(currentItem);
    if (!el) return;
    const snap = !skipNextSnapRef.current;
    skipNextSnapRef.current = false;
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (snap) {
      const container = scrollRef.current;
      if (container) scrollIntoViewCentered(container, el, !reduced);
      else el.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
    }
    el.focus({ preventScroll: true });
  }, [reader, safeCursor, currentItem, elementFor]);

  const goto = useCallback(
    (next: number) => {
      if (navCount === 0) return;
      const idx = ((next % navCount) + navCount) % navCount;
      // Keyboard and the prev/next buttons move ONE cursor and collapse the
      // selection onto it — Explorer's arrow-key behaviour.
      setCursor(idx);
      setSelection([idx]);
      setAnchor(idx);
    },
    [navCount]
  );

  /** One decision, fanned out across the selection — but only to the items
      whose kind accepts it (headings to unstyled text, Remove/Keep to blanks
      and breaks). A single-item selection keeps the guided flow: decide and
      advance. A batch stays put, so the user can keep refining it. */
  const decideSelection = useCallback(
    (d: DirtyDecision) => {
      const targets = selectedItems.filter((item) => item.id && decisionAppliesTo(item, d.action));
      for (const item of targets) onDecide(activeFile, item.id as string, d);
      if (selectedItems.length <= 1) goto(safeCursor + 1);
    },
    [selectedItems, onDecide, activeFile, goto, safeCursor]
  );

  /** What the fixed Accept slot will do to the current selection. */
  const acceptPlan = useMemo(() => acceptSuggestedPlan(selectedItems), [selectedItems]);

  /** The Accept slot earns its place only when it says something the verb
      buttons below don't already say: an unheadered paragraph's suggestion
      picks a SPECIFIC heading level, which is real information. For blocks
      (empty-paragraph/page-break) and terminators (stale-terminator/
      pre-body-remnant) the suggestion IS the kind's only non-Keep verb, so
      "Accept · Delete" next to a Delete button is the same button twice —
      the verb button alone carries the primary styling instead (see
      ItemActions). A selection mixing heading items with the other kinds
      still shows the slot, since the heading portion needs it. */
  const showAcceptSlot = useMemo(
    () => selectedItems.some((i) => categoryOf(itemKind(i)) === 'heading'),
    [selectedItems]
  );

  /** Where a spam-click lands next: the first item still undecided, wrapping,
      counting whatever this very click just decided. Falls back to plain
      "next" once nothing is left, so the flow can never stall on the last
      item. Only Accept uses it — Skip and the H buttons keep the step-by-one
      navigation they have always had. */
  const gotoNextUnresolved = useCallback(
    (from: number, justDecided: Set<string>) => {
      if (navCount === 0) return;
      for (let step = 1; step <= navCount; step++) {
        const idx = (from + step) % navCount;
        const id = items[idx]?.id;
        if (!id || !(justDecided.has(id) || fileDecisions[id])) {
          goto(idx);
          return;
        }
      }
      goto(from + 1);
    },
    [navCount, items, fileDecisions, goto]
  );

  const acceptSuggested = useCallback(() => {
    if (acceptPlan.targets.length === 0) return;
    const decided = new Set<string>();
    for (const target of acceptPlan.targets) {
      onDecide(activeFile, target.id, target.decision);
      decided.add(target.id);
    }
    // A batch stays put so the user can keep refining it — the same rule the
    // H1-H9 buttons follow. One item advances, which is the spam-through.
    if (selectedItems.length <= 1) gotoNextUnresolved(safeCursor, decided);
  }, [acceptPlan, onDecide, activeFile, selectedItems.length, gotoNextUnresolved, safeCursor]);

  // Clicking a highlighted paragraph selects it — Windows Explorer semantics:
  // plain replaces, Ctrl toggles, Shift ranges from the anchor, Ctrl+Shift
  // adds a range. Unflagged paragraphs are inert.
  const onDocClick = useCallback(
    (e: React.MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest?.('[data-sf-item]');
      if (!target) return;
      const idx = Number(target.getAttribute('data-sf-item'));
      if (!Number.isInteger(idx)) return;
      const next = nextSelection(selectedIndices, anchor, idx, {
        ctrl: e.ctrlKey || e.metaKey,
        shift: e.shiftKey,
      });
      skipNextSnapRef.current = true;
      // An empty result (Ctrl-click on the sole selection) is legal: the
      // derived selection falls back to the cursor, which is this paragraph.
      setSelection(next.selection);
      setAnchor(next.anchor);
      setCursor(idx);
    },
    [selectedIndices, anchor]
  );

  const readerFailed = reader.status === 'error';
  const mappingLost = reader.status === 'ready' && navCount > 0 && offsetRef.current == null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      {open && (
        <DialogContent
          className="max-w-6xl"
          aria-describedby={undefined}
          style={{ height: '88vh', gridTemplateRows: 'auto auto 1fr auto' }}
          onKeyDown={(e: React.KeyboardEvent) => {
            // Ctrl/⌘ + ↑/↓ jumps between flagged items; plain arrows stay free
            // to scroll the document, exactly as in Word.
            if ((e.ctrlKey || e.metaKey) && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              goto(safeCursor + (e.key === 'ArrowDown' ? 1 : -1));
              return;
            }
            // Enter = the fixed Accept slot, so the spam-through has a
            // keyboard twin. Never while a control has focus: that control's
            // own Enter is the one the user meant.
            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.altKey && !readOnly) {
              const target = e.target as HTMLElement | null;
              if (target?.closest?.('button, a, input, textarea, select, [role="button"]')) return;
              e.preventDefault();
              acceptSuggested();
              return;
            }
            // More keys (2026-09-08), each a keyboard twin of a button
            // already in the bar. Never while typing in an editable
            // control — that control's own key is the one the user meant
            // (buttons themselves stay live: K on a focused Keep button
            // still fires Keep, same as Enter fires whatever button has
            // focus natively).
            if (readOnly || e.ctrlKey || e.metaKey || e.altKey) return;
            const target = e.target as HTMLElement | null;
            if (target?.closest?.('input, textarea, select, [contenteditable]')) return;
            if (/^[1-9]$/.test(e.key)) {
              if (
                !selectedItems.some((i) => ['heading', 'typed'].includes(categoryOf(itemKind(i))))
              )
                return; // no level-able item selected — no-op
              e.preventDefault();
              decideSelection({ action: 'fix', heading: `Heading${e.key}` });
              return;
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
              if (
                !selectedItems.some(
                  (i) =>
                    categoryOf(itemKind(i)) === 'block' ||
                    itemKind(i) === 'stale-terminator' ||
                    itemKind(i) === 'pre-body-remnant'
                )
              )
                return;
              e.preventDefault();
              decideSelection({ action: 'remove' });
              return;
            }
            if (e.key === 'k' || e.key === 'K') {
              e.preventDefault();
              decideSelection({ action: 'keep' });
              return;
            }
            if (e.key === 'f' || e.key === 'F') {
              e.preventDefault();
              decideSelection({ action: 'flag' });
              return;
            }
            if ((e.key === 's' || e.key === 'S') && selectedItems.length === 1) {
              e.preventDefault();
              goto(safeCursor + 1);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {readOnly ? 'Formatting Artifacts (read-only)' : 'Formatting Artifacts'}
            </DialogTitle>
          </DialogHeader>

          {/* Toolbar — which file, where in it */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            {files.length > 1 ? (
              <div
                style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}
                role="tablist"
              >
                {files.map((f) => {
                  const undecided = countUndecided(
                    f.unheadered?.samples || [],
                    decisions[f.file] || {}
                  );
                  return (
                    <Button
                      key={f.file}
                      type="button"
                      role="tab"
                      aria-selected={f.file === activeFile}
                      variant={f.file === activeFile ? 'default' : 'outline'}
                      size="sm"
                      title={f.file}
                      onClick={() => setActiveFile(f.file)}
                    >
                      {baseName(f.file)}
                      {undecided > 0 ? ` (${undecided})` : ''}
                    </Button>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-bold)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  overflowWrap: 'anywhere',
                  minWidth: 0,
                }}
                title={activeFile}
              >
                <Icon
                  icon={FileText}
                  em={1.0769}
                  aria-hidden="true"
                  style={{ opacity: 0.7, flexShrink: 0 }}
                />
                {baseName(activeFile)}
              </div>
            )}

            {autoFixedCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-expanded={showAutoFixed}
                onClick={() => setShowAutoFixed((v) => !v)}
              >
                Auto-fixed ({autoFixedCount})
              </Button>
            )}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-expanded={showRules}
              onClick={() => setShowRules((v) => !v)}
            >
              Rules
            </Button>

            <div style={{ flex: 1 }} />

            <ZoomControl
              value={Math.round(readerZoom * 100)}
              onChange={(pct) => setReaderZoom(clampReaderZoom(pct / 100))}
              min={Math.round(READER_ZOOM_MIN * 100)}
              max={Math.round(READER_ZOOM_MAX * 100)}
              step={Math.round(READER_ZOOM_STEP * 100)}
            />

            {navCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span
                  style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-muted)' }}
                  aria-live="polite"
                >
                  {safeCursor + 1} of {navCount}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label="Previous flagged item"
                  onClick={() => goto(safeCursor - 1)}
                >
                  <Icon icon={ChevronUp} em={1.4} aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label="Next flagged item"
                  onClick={() => goto(safeCursor + 1)}
                >
                  <Icon icon={ChevronDown} em={1.4} aria-hidden="true" />
                </Button>
              </div>
            )}
          </div>

          {/* Document surface — the reader, its states, and the floating bar */}
          <div style={{ position: 'relative', minHeight: 0 }}>
            <div
              ref={readerNodeRef}
              className={`sf-docx-reader${readerFailed ? '' : ' sf-docx-reader--guided'}`}
              onClick={onDocClick}
              style={{
                position: 'absolute',
                inset: 0,
                overflow: 'auto',
                background: 'var(--color-bg-subtle)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                visibility: readerFailed ? 'hidden' : undefined,
              }}
            >
              {showRules && (
                <div style={{ padding: 'var(--space-4) var(--space-4) 0' }}>
                  <FormatterRulesLegend />
                </div>
              )}
              {showAutoFixed && autoFixedCount > 0 && (
                <div style={{ padding: 'var(--space-4) var(--space-4) 0' }}>
                  <AutoFixedList fixed={autoFixed} />
                </div>
              )}
              <div ref={styleRef} hidden />
              <div className="sf-docx-reader-scale" style={{ transform: `scale(${readerScale})` }}>
                <div ref={docRef} />
              </div>
            </div>

            {reader.status === 'loading' && (
              <div
                role="status"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-2)',
                  background: 'var(--color-bg-subtle)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--font-size-base)',
                }}
              >
                <Icon icon={Loader2} em={1.2308} aria-hidden="true" className="animate-spin" />
                Opening the formatted document…
              </div>
            )}

            {readerFailed && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  overflow: 'auto',
                  background: 'var(--color-bg-subtle)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-4)',
                }}
              >
                <div
                  role="status"
                  style={{
                    marginBottom: 'var(--space-3)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--color-warning)',
                  }}
                >
                  Can’t show the document here ({reader.message}) — review the flagged items on
                  their own instead.
                </div>
                <div
                  style={{
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--space-4)',
                  }}
                >
                  <DirtyFileGroup
                    file={activeFile}
                    unheadered={activeResult?.unheadered || { total: 0, samples: [] }}
                    decisions={fileDecisions}
                    readOnly={readOnly}
                    onDecide={(id, d) => onDecide(activeFile, id, d)}
                  />
                </div>
              </div>
            )}

            {/* Floating decision bar — docked over the document, never inline
                in it, so the document is never re-flowed by app chrome. */}
            {!readerFailed && currentItem && (
              <div
                style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  right: 'var(--space-3)',
                  bottom: 'var(--space-3)',
                  zIndex: 20,
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-lg)',
                  padding: 'var(--space-3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    minWidth: 0,
                  }}
                >
                  <StatusBadge
                    status="dirty-kind"
                    label={
                      selectedItems.length > 1
                        ? `${selectedItems.length} selected`
                        : kindLabel({
                            kind: itemKind(currentItem),
                            tableAdjacent: currentItem.tableAdjacent,
                            hasLevel: currentItem.hasLevel,
                          })
                    }
                    variant={
                      selectedItems.length > 1
                        ? 'info'
                        : itemKind(currentItem) === 'unheadered'
                          ? 'warning'
                          : 'info'
                    }
                  />
                  <div
                    data-sf-bar-snippet=""
                    aria-live="polite"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 'var(--font-size-base)',
                      color:
                        selectedItems.length > 1 || currentItem.text
                          ? 'var(--color-text)'
                          : 'var(--color-text-muted)',
                      fontStyle:
                        selectedItems.length === 1 && !currentItem.text ? 'italic' : undefined,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={
                      selectedItems.length > 1
                        ? selectionSummary(selectedItems)
                        : itemSnippet(currentItem)
                    }
                  >
                    {selectedItems.length > 1
                      ? selectionSummary(selectedItems)
                      : itemSnippet(currentItem)}
                  </div>
                  {selectedItems.length === 1 &&
                    (readOnly || !!(currentItem.id && fileDecisions[currentItem.id])) &&
                    decisionBadge(currentItem.id ? fileDecisions[currentItem.id] : undefined)}
                </div>

                {mappingLost && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    This item couldn’t be located in the saved document — decide it here; the fix
                    still targets the right paragraph.
                  </div>
                )}

                {!readOnly && (
                  /* The Accept slot is bottom-aligned in its own fixed-width
                     column, and the bar itself is anchored to the bottom of
                     the reader — so the slot's rectangle is identical for
                     every item, whatever the rest of the bar does above it. */
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 'var(--space-2)',
                      minWidth: 0,
                    }}
                  >
                    {showAcceptSlot && (
                      <AcceptSlot
                        label={acceptPlan.label}
                        disabled={acceptPlan.targets.length === 0}
                        onAccept={acceptSuggested}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ItemActions
                        items={selectedItems}
                        decisions={fileDecisions}
                        onDecide={decideSelection}
                        onSkip={() => goto(safeCursor + 1)}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {!readerFailed && reader.status === 'ready' && navCount === 0 && (
              <div
                style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  right: 'var(--space-3)',
                  bottom: 'var(--space-3)',
                  zIndex: 20,
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-lg)',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--color-text-muted)',
                }}
              >
                Nothing left to decide in this file.
              </div>
            )}
          </div>

          <DialogFooter>
            {!readOnly && totalFlagged > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={onKeepAllRemaining}
                disabled={totalUndecided === 0}
              >
                Keep all remaining{totalUndecided > 0 ? ` (${totalUndecided})` : ''}
              </Button>
            )}
            {!readOnly && blankReturnsLeft > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteKind('empty-paragraph')}
              >
                Delete all blank returns ({blankReturnsLeft})
              </Button>
            )}
            {!readOnly && pageBreaksLeft > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteKind('page-break')}
              >
                Delete all page breaks ({pageBreaksLeft})
              </Button>
            )}
            {totalFlaggedForEditing > 0 && (
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                {totalFlaggedForEditing} flagged for editing
              </span>
            )}
            {/* DESIGN §3.3 — exactly one primary CTA. In the live review the
                primary lives in the decision bar (the item you are on IS the
                job); Done just ends the pass. With nothing left to decide, or
                in read-only, Done becomes the primary. */}
            <Button
              size="sm"
              variant={readOnly || totalUndecided === 0 ? 'default' : 'secondary'}
              onClick={onClose}
            >
              {readOnly ? 'Close' : 'Done'}
            </Button>
          </DialogFooter>
          <ConfirmDialog
            open={bulkDeleteKind !== null}
            onOpenChange={(o) => {
              if (!o) setBulkDeleteKind(null);
            }}
            title={
              bulkDeleteKind === 'page-break'
                ? 'Delete all page breaks?'
                : 'Delete all blank returns?'
            }
            description={
              bulkDeleteKind === 'page-break'
                ? `Marks ${pageBreaksLeft} undecided manual page ${pageBreaksLeft === 1 ? 'break' : 'breaks'} for deletion. Items you already decided stay as you decided them. Nothing is deleted until you format.`
                : `Marks ${blankReturnsLeft} undecided blank ${blankReturnsLeft === 1 ? 'return' : 'returns'} for deletion. Items you already decided stay as you decided them. Nothing is deleted until you format.`
            }
            confirmLabel="Delete"
            destructive
            onConfirm={confirmBulkDelete}
          />
        </DialogContent>
      )}
    </Dialog>
  );
}

export { keepDecisionFor };
export default DirtyReviewDialog;
